import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from '../assets/expenditures.module.css';
import { expendituresService } from '../services/expendituresService';
// P2P v1 — read-only payment status from Xero
import { getPaymentStatus } from '../services/xeroBillsService';

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const Expenditures = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // State for filters
  const [filters, setFilters] = useState({
    category_id: '',
    type_id: '',
    bank_account_id: '',
    creditor: '',
    date_from: '',
    date_to: ''
  });

  // Applied filters state - only updated when search is clicked
  const [appliedFilters, setAppliedFilters] = useState({
    category_id: '',
    type_id: '',
    bank_account_id: '',
    creditor: '',
    date_from: '',
    date_to: ''
  });

  // State for DataTables controls
  const [tableControls, setTableControls] = useState({
    entriesPerPage: '30',
    search: ''
  });

  // Applied search state - updated on change
  const [appliedTableSearch, setAppliedTableSearch] = useState('');

  // State for data
  const [expenditures, setExpenditures] = useState([]);
  const [filteredExpenditures, setFilteredExpenditures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false); // Separate loading state for table updates
  const [useMockData, setUseMockData] = useState(false);

  // State for dropdowns
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // State for column visibility
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({
    property: true,
    category: true,
    type: true,
    bankAccount: true,
    amountPaid: true,
    date: true,
    creditor: true,
    invoice: true,
    ticketId: true,
    order: true,
    // P2P v1 — what Xero says about payment. Read-only; MyGlobe's own amount_paid is
    // untouched, so the two can be compared rather than one silently replacing the other.
    //
    // Off by default. Without the Xero Bills permission the API refuses and the column shows
    // nothing but dashes, so for anyone still working the existing way it is a blank column
    // on a screen they use daily. Whoever wants it turns it on from Columns.
    xeroPayment: false,
    actions: true
  });

  // Xero payment status for the rows currently on screen, keyed by expenditure id.
  const [xeroPayments, setXeroPayments] = useState({});

  // State for sorting
  const [sortField, setSortField] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");

  // Refs for search input focus preservation
  const searchInputRef = useRef(null);
  const shouldMaintainFocusRef = useRef(false);
  const cursorPositionRef = useRef(null);
  // Ref to store debounce timer for API calls
  const searchDebounceTimer = useRef(null);

  // Generate mock data
  const generateMockExpenditures = () => {
    const mockExpenditures = [];
    const categories = ['Maintenance', 'Utilities', 'Insurance', 'Repairs', 'Cleaning'];
    const types = ['Regular', 'Emergency', 'Planned', 'Preventive', 'Reactive'];
    const bankAccounts = ['Main Account', 'Savings', 'Operations', 'Emergency Fund'];
    const creditors = ['ABC Plumbing', 'XYZ Electric', 'City Water', 'State Gas', 'Local Insurance'];
    const properties = ['Sunset Apartments', 'Downtown Lofts', 'Garden Heights', 'Riverside Complex', 'Mountain View'];

    for (let i = 1; i <= 50; i++) {
      const category = categories[Math.floor(Math.random() * categories.length)];
      const type = types[Math.floor(Math.random() * types.length)];
      const bankAccount = bankAccounts[Math.floor(Math.random() * bankAccounts.length)];
      const creditor = creditors[Math.floor(Math.random() * creditors.length)];
      const property = properties[Math.floor(Math.random() * properties.length)];
      const amount = Math.floor(Math.random() * 5000) + 100;
      const date = new Date(2024, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1);
      const hasFile = Math.random() > 0.5;
      const isVoid = Math.random() > 0.8;

      mockExpenditures.push({
        id: i,
        propertyId: i,
        propertyName: property,
        categoryName: category,
        typeName: type,
        bankAccountName: bankAccount,
        amount_paid: amount,
        date: date.toISOString().split('T')[0],
        creditor: creditor,
        invoice_number: `INV-${String(i).padStart(4, '0')}`,
        ticket_id: Math.random() > 0.3 ? Math.floor(Math.random() * 1000) + 1 : null,
        purchase_order_number: `PO-${String(i).padStart(4, '0')}`,
        file: hasFile ? `file-${i}.pdf` : null,
        void: isVoid,
        status: Math.random() > 0.5 ? 'paid' : 'pending'
      });
    }
    return mockExpenditures;
  };

  const generateMockDropdowns = () => {
    return {
      categories: [
        { id: 1, name: 'Maintenance' },
        { id: 2, name: 'Utilities' },
        { id: 3, name: 'Insurance' },
        { id: 4, name: 'Repairs' },
        { id: 5, name: 'Cleaning' }
      ],
      types: [
        { id: 1, name: 'Regular' },
        { id: 2, name: 'Emergency' },
        { id: 3, name: 'Planned' },
        { id: 4, name: 'Preventive' },
        { id: 5, name: 'Reactive' }
      ],
      bankAccounts: [
        { id: 1, name: 'Main Account' },
        { id: 2, name: 'Savings' },
        { id: 3, name: 'Operations' },
        { id: 4, name: 'Emergency Fund' }
      ]
    };
  };

  // Load dropdown data
  const loadDropdownData = async () => {
    try {
      console.log('[EXPENDITURES] Loading dropdown data...');
      
      if (useMockData) {
        const mockDropdowns = generateMockDropdowns();
        setCategories(mockDropdowns.categories);
        setTypes(mockDropdowns.types);
        setBankAccounts(mockDropdowns.bankAccounts);
        console.log('[EXPENDITURES] Mock dropdown data loaded');
      } else {
        // Make separate API calls for dropdown data
        const [categoriesResponse, typesResponse, bankAccountsResponse] = await Promise.all([
          expendituresService.getCategories(),
          expendituresService.getTypes(),
          expendituresService.getBankAccounts()
        ]);

        console.log('[EXPENDITURES] API responses:', {
          categories: categoriesResponse,
          types: typesResponse,
          bankAccounts: bankAccountsResponse
        });

        setCategories(categoriesResponse.data || []);
        setTypes(typesResponse.data || []);
        setBankAccounts(bankAccountsResponse.data || []);
        console.log('[EXPENDITURES] Real dropdown data loaded');
      }
    } catch (error) {
      console.error('[EXPENDITURES] Error loading dropdown data:', error);
      // Fallback to mock data if API fails
      const mockDropdowns = generateMockDropdowns();
      setCategories(mockDropdowns.categories);
      setTypes(mockDropdowns.types);
      setBankAccounts(mockDropdowns.bankAccounts);
      console.log('[EXPENDITURES] Fallback to mock dropdown data');
    }
  };

  // Load data with server-side pagination (matching Laravel)
  const loadExpenditures = useCallback(async (isInitialLoad = false) => {
    try {
      // Use tableLoading for subsequent loads to avoid full page re-render
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setTableLoading(true);
      }
      if (useMockData) {
        const mockData = generateMockExpenditures();
        // Client-side pagination for mock data
        const entriesPerPage = parseInt(tableControls.entriesPerPage, 10) || 30;
        const startIndex = (currentPage - 1) * entriesPerPage;
        const endIndex = startIndex + entriesPerPage;
        const paginatedData = mockData.slice(startIndex, endIndex);
        setExpenditures(paginatedData);
        setTotalRecords(mockData.length);
      } else {
        // Server-side pagination (matching Laravel DataTables)
        const entriesPerPage = parseInt(tableControls.entriesPerPage, 10) || 30;
        const start = (currentPage - 1) * entriesPerPage;
        
        const requestData = {
          ...appliedFilters,
          search: { value: appliedTableSearch || '' },
          start: start,
          length: entriesPerPage,
          draw: 1
        };
        
        const response = await expendituresService.getExpenditures(requestData);
        
        // Handle DataTables response format
        if (response.data && Array.isArray(response.data)) {
          setExpenditures(response.data);
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          setExpenditures(response.data.data);
        } else {
          setExpenditures([]);
        }
        
        // Use recordsFiltered for filtered count, recordsTotal for total count
        setTotalRecords(response.recordsFiltered || response.recordsTotal || 0);
      }
    } catch (error) {
      console.error('Error loading expenditures:', error);
      toast.error('Failed to load expenditures');
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  }, [useMockData, currentPage, tableControls.entriesPerPage, appliedFilters, appliedTableSearch]);

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // For server-side pagination, expenditures are already filtered and paginated by backend
  // Only apply client-side sorting if needed (backend should handle sorting, but we keep this for flexibility)
  const getFilteredExpenditures = useMemo(() => {
    // Server-side: expenditures are already filtered and paginated by backend
    // Only apply sorting if backend doesn't handle it
    let sorted = [...expenditures];

    // Apply sorting (backend should handle this, but keep for client-side fallback)
    return sorted.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle nested object sorting
      if (sortField === "property") {
        aValue = a.propertyName || "";
        bValue = b.propertyName || "";
      } else if (sortField === "category") {
        aValue = a.categoryName || "";
        bValue = b.categoryName || "";
      } else if (sortField === "type") {
        aValue = a.typeName || "";
        bValue = b.typeName || "";
      } else if (sortField === "bankAccount") {
        aValue = a.bankAccountName || "";
        bValue = b.bankAccountName || "";
      } else if (sortField === "amountPaid") {
        aValue = a.amount_paid || 0;
        bValue = b.amount_paid || 0;
      } else if (sortField === "date") {
        aValue = a.date;
        bValue = b.date;
      } else if (sortField === "creditor") {
        aValue = a.creditor || "";
        bValue = b.creditor || "";
      } else if (sortField === "invoice") {
        aValue = a.invoice_number || "";
        bValue = b.invoice_number || "";
      } else if (sortField === "ticketId") {
        aValue = a.ticket_id || "";
        bValue = b.ticket_id || "";
      } else if (sortField === "order") {
        aValue = a.purchase_order_number || "";
        bValue = b.purchase_order_number || "";
      }

      // Handle date sorting
      if (sortField === "date") {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [expenditures, sortField, sortDirection]);

  // Pagination logic (server-side pagination)
  useEffect(() => {
    const entriesPerPage = parseInt(tableControls.entriesPerPage, 10) || 30;
    const totalPages = Math.ceil(totalRecords / entriesPerPage);
    const validCurrentPage = Math.min(Math.max(1, currentPage), totalPages || 1);
    
    if (validCurrentPage !== currentPage && totalPages > 0) {
      setCurrentPage(validCurrentPage);
    }
    
    setTotalPages(totalPages);
  }, [totalRecords, tableControls.entriesPerPage, currentPage]);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [appliedTableSearch]);

  // Restore focus after every render if input should maintain focus
  useEffect(() => {
    if (shouldMaintainFocusRef.current && searchInputRef.current) {
      // Use setTimeout to ensure this runs after React's render cycle
      const timeoutId = setTimeout(() => {
        if (searchInputRef.current && shouldMaintainFocusRef.current) {
          searchInputRef.current.focus();
          // Restore cursor position if we have it stored
          if (cursorPositionRef.current !== null) {
            searchInputRef.current.setSelectionRange(
              cursorPositionRef.current,
              cursorPositionRef.current
            );
          } else {
            // Otherwise, set cursor to end
            const length = searchInputRef.current.value.length;
            searchInputRef.current.setSelectionRange(length, length);
          }
        }
      }, 0);
      
      return () => clearTimeout(timeoutId);
    }
  }); // Run after every render

  // Get current page data (for server-side pagination, data is already paginated)
  const getCurrentPageData = () => {
    // Server-side: expenditures are already paginated by backend
    // Only apply sorting if needed
    return getFilteredExpenditures;
  };

  // Load dropdown data on mount and when useMockData changes
  useEffect(() => {
    loadDropdownData();
  }, [useMockData]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    };
  }, []);

  // Track if component has mounted to avoid double loading
  const [hasMounted, setHasMounted] = useState(false);
  const isInitialLoadRef = useRef(true);

  // Load data on mount and when pagination/filters change (server-side pagination)
  useEffect(() => {
    // Pass isInitialLoad=true only on first mount
    loadExpenditures(isInitialLoadRef.current);
    isInitialLoadRef.current = false;
    setHasMounted(true);
  }, [loadExpenditures]);

  // Reload data when navigating back to this page (e.g., after editing)
  useEffect(() => {
    // Only reload if component has already mounted (to avoid double loading on initial mount)
    if (hasMounted && location.pathname === '/expenditures') {
      loadExpenditures();
    }
  }, [location.pathname, hasMounted]);

  // Handlers
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleTableControlChange = (field, value) => {
    setTableControls(prev => ({ ...prev, [field]: value }));

    // If it's a search field, debounce the API call (server-side filtering needs debouncing)
    if (field === 'search') {
      // Clear existing debounce timer
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }

      // Debounce API call (server-side filtering needs debouncing)
      const searchValue = value ? String(value).trim() : "";
      searchDebounceTimer.current = setTimeout(() => {
        // Only update appliedTableSearch when debounce completes - prevents re-renders during typing
        setAppliedTableSearch(searchValue);
        setCurrentPage(1);
        loadExpenditures();
      }, 300);
    }
  };

  // Handle search input focus
  const handleSearchFocus = () => {
    shouldMaintainFocusRef.current = true;
    if (searchInputRef.current) {
      cursorPositionRef.current = searchInputRef.current.selectionStart;
    }
  };

  // Handle search input blur
  const handleSearchBlur = () => {
    // Only stop maintaining focus if user explicitly clicked away
    // Don't stop if it's just a rerender
    setTimeout(() => {
      if (searchInputRef.current !== document.activeElement) {
        shouldMaintainFocusRef.current = false;
        cursorPositionRef.current = null;
      }
    }, 0);
  };

  // Handle search input change and track cursor position
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    // Store cursor position before state update
    cursorPositionRef.current = e.target.selectionStart;
    // Ensure we maintain focus
    shouldMaintainFocusRef.current = true;
    handleTableControlChange('search', value);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setAppliedFilters(filters); // Apply the current filters
    setAppliedTableSearch(tableControls.search); // Apply the current search
    loadExpenditures(); // Trigger filtering with current filter values
  };

  const handleClearFilters = () => {
    setFilters({
      category_id: '',
      type_id: '',
      bank_account_id: '',
      creditor: '',
      date_from: '',
      date_to: ''
    });
    setTableControls(prev => ({ ...prev, search: '' }));
    setAppliedFilters({
      category_id: '',
      type_id: '',
      bank_account_id: '',
      creditor: '',
      date_from: '',
      date_to: ''
    });
    setAppliedTableSearch('');
    setCurrentPage(1);
    // Use setTimeout to ensure state updates before loading expenses
    setTimeout(() => {
      loadExpenditures();
    }, 0);
  };

  const handleColumnVisibility = () => {
    setShowColumnVisibility(!showColumnVisibility);
  };

  const toggleColumnVisibility = (column) => {
    setColumnVisibility(prev => ({ ...prev, [column]: !prev[column] }));
  };

  const handleExportCSV = async () => {
    try {
      const filtered = getFilteredExpenditures;
      
      // Create CSV content with all visible columns
      const headers = [];
      const dataRows = [];
      
      // Add headers based on column visibility
      if (columnVisibility.property) headers.push('Property');
      if (columnVisibility.category) headers.push('Category');
      if (columnVisibility.type) headers.push('Type');
      if (columnVisibility.bankAccount) headers.push('Bank Account');
      if (columnVisibility.amountPaid) headers.push('Amount Paid');
      if (columnVisibility.date) headers.push('Date');
      if (columnVisibility.creditor) headers.push('Creditor');
      if (columnVisibility.invoice) headers.push('Invoice');
      if (columnVisibility.ticketId) headers.push('Ticket ID');
      if (columnVisibility.order) headers.push('Order');
      
      // Add data rows
      filtered.forEach(expenditure => {
        const row = [];
        if (columnVisibility.property) row.push(expenditure.propertyName);
        if (columnVisibility.category) row.push(expenditure.categoryName);
        if (columnVisibility.type) row.push(expenditure.typeName);
        if (columnVisibility.bankAccount) row.push(expenditure.bankAccountName);
        if (columnVisibility.amountPaid) row.push(formatCurrency(expenditure.amount_paid));
        if (columnVisibility.date) row.push(formatDate(expenditure.date));
        if (columnVisibility.creditor) row.push(expenditure.creditor);
        if (columnVisibility.invoice) row.push(expenditure.invoice_number);
        if (columnVisibility.ticketId) row.push(expenditure.ticket_id || '');
        if (columnVisibility.order) row.push(expenditure.purchase_order_number);
        dataRows.push(row);
      });
      
      const csvContent = [
        headers.join(','),
        ...dataRows.map(row => row.map(field => `"${field}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `expenditures-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('CSV exported successfully!');
    } catch (error) {
      console.error('Export CSV error:', error);
      toast.error('Failed to export CSV');
    }
  };

    const handleExportPDF = async () => {
    try {
      const filtered = getFilteredExpenditures;
      
      // Create PDF content using jsPDF (copied from ExpenseAccount.jsx)
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(16);
      doc.text('Expenditures Report', 20, 20);
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString('en-GB')}`, 20, 30);
      doc.text(`Total Records: ${filtered.length}`, 20, 40);
      
      // Calculate total amount
      const totalAmount = filtered.reduce((sum, expenditure) => sum + parseFloat(expenditure.amount_paid || 0), 0);
      doc.text(`Total Amount: £${totalAmount.toFixed(2)}`, 20, 50);
      
      // Add table headers
      const headers = [];
      if (columnVisibility.property) headers.push('Property');
      if (columnVisibility.category) headers.push('Category');
      if (columnVisibility.type) headers.push('Type');
      if (columnVisibility.bankAccount) headers.push('Bank Account');
      if (columnVisibility.amountPaid) headers.push('Amount Paid');
      if (columnVisibility.date) headers.push('Date');
      if (columnVisibility.creditor) headers.push('Creditor');
      if (columnVisibility.invoice) headers.push('Invoice');
      if (columnVisibility.ticketId) headers.push('Ticket ID');
      if (columnVisibility.order) headers.push('Order');
      
      let yPosition = 70;
      const startX = 20;
      const colWidth = 20;
      
      // Draw headers
      headers.forEach((header, index) => {
        doc.text(header, startX + (index * colWidth), yPosition);
      });
      
      yPosition += 10;
      
      // Draw data rows
      filtered.forEach((expenditure, index) => {
        if (yPosition > 280) {
          doc.addPage();
          yPosition = 20;
        }
        
        const rowData = [];
        if (columnVisibility.property) rowData.push(expenditure.propertyName ? expenditure.propertyName.substring(0, 15) : '');
        if (columnVisibility.category) rowData.push(expenditure.categoryName ? expenditure.categoryName.substring(0, 15) : '');
        if (columnVisibility.type) rowData.push(expenditure.typeName ? expenditure.typeName.substring(0, 15) : '');
        if (columnVisibility.bankAccount) rowData.push(expenditure.bankAccountName ? expenditure.bankAccountName.substring(0, 15) : '');
        if (columnVisibility.amountPaid) rowData.push(formatCurrency(expenditure.amount_paid));
        if (columnVisibility.date) rowData.push(formatDate(expenditure.date));
        if (columnVisibility.creditor) rowData.push(expenditure.creditor ? expenditure.creditor.substring(0, 15) : '');
        if (columnVisibility.invoice) rowData.push(expenditure.invoice_number ? expenditure.invoice_number.substring(0, 15) : '');
        if (columnVisibility.ticketId) rowData.push(expenditure.ticket_id ? expenditure.ticket_id.toString().substring(0, 15) : '');
        if (columnVisibility.order) rowData.push(expenditure.purchase_order_number ? expenditure.purchase_order_number.substring(0, 15) : '');
        
        rowData.forEach((cell, cellIndex) => {
          doc.text(cell.substring(0, 18), startX + (cellIndex * colWidth), yPosition);
        });
        
        yPosition += 7;
      });
      
      // Save PDF
      doc.save(`expenditures-${new Date().toISOString().split('T')[0]}.pdf`);
      
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    }
  };

  const handlePrintTable = () => {
    try {
      const filtered = getFilteredExpenditures;
      const printWindow = window.open('', '_blank');
      
      const printHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Expenditures - Print</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              font-size: 12px;
            }
            h1 { 
              color: #333; 
              text-align: center; 
              margin-bottom: 20px; 
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-bottom: 20px; 
            }
            th, td { 
              border: 1px solid #ddd; 
              padding: 8px; 
              text-align: left; 
            }
            th { 
              background-color: #f8f9fa; 
              font-weight: bold; 
            }
            .header { 
              margin-bottom: 20px; 
            }
            .date { 
              text-align: right; 
              color: #666; 
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Expenditures Report</h1>
            <div class="date">Generated on: ${new Date().toLocaleDateString('en-GB')}</div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Property</th>
                <th>Category</th>
                <th>Type</th>
                <th>Bank Account</th>
                <th>Amount Paid</th>
                <th>Date</th>
                <th>Creditor</th>
                <th>Invoice</th>
                <th>Ticket ID</th>
                <th>Order</th>
              </tr>
            </thead>
            <tbody>
              ${filtered.map(expenditure => `
                <tr>
                  <td>${expenditure.propertyName}</td>
                  <td>${expenditure.categoryName}</td>
                  <td>${expenditure.typeName}</td>
                  <td>${expenditure.bankAccountName}</td>
                  <td>£${parseFloat(expenditure.amount_paid).toFixed(2)}</td>
                  <td>${formatDate(expenditure.date)}</td>
                  <td>${expenditure.creditor}</td>
                  <td>${expenditure.invoice_number}</td>
                  <td>${expenditure.ticket_id || ''}</td>
                  <td>${expenditure.purchase_order_number}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="no-print">
            <button onclick="window.print()">Print</button>
            <button onclick="window.close()">Close</button>
          </div>
        </body>
        </html>
      `;

      printWindow.document.write(printHTML);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
      
      toast.success('Print window opened');
    } catch (error) {
      console.error('Error printing table:', error);
      toast.error('Failed to print table');
    }
  };

  const handleShow = (id) => {
    navigate(`/expenditures/${id}`);
  };

  const handleEdit = (expenditure) => {
    // Navigate to ExpenditureItemEdit page (newly created component)
    navigate(`/expenditures/${expenditure.id}/edit`);
  };

  const handleDownload = async (expenditure) => {
    try {
      // Find the expenditure in the current data
      const expense = expenditures.find(exp => exp.id === expenditure.id);
      if (!expense) {
        toast.error('Expenditure not found');
        return;
      }

      // Check if file exists
      if (!expense.file) {
        toast.error('No file available for this expenditure');
        return;
      }

      // Download file using expendituresService (which handles presigned URLs correctly)
      try {
        const response = await expendituresService.downloadFile(expense.id, 'file');
        if (response && response.success) {
          toast.success('File download started!');
        } else {
          throw new Error('Failed to download file');
        }
      } catch (error) {
        console.error('File download error:', error);
        toast.error('Failed to download file. Please try again.');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download expenditure file');
    }
  };

  const handleRemove = async (expenditure) => {
    if (window.confirm(`Are you sure you want to remove expenditure "${expenditure.invoice_number}" and all related records? This action cannot be undone.`)) {
      try {
        if (useMockData) {
          // For mock data, remove from local state
          setExpenditures(prev => prev.filter(item => item.id !== expenditure.id));
          toast.success(`Expenditure "${expenditure.invoice_number}" and all related records deleted successfully!`);
        } else {
          // For real API - delete all related records
          // Get propertyId from expenditure (propertyId or property_id)
          const propertyId = expenditure.propertyId || expenditure.property_id;
          
          if (!propertyId) {
            toast.error(`Cannot delete expenditure: Property ID not found for expenditure "${expenditure.invoice_number}"`);
            return;
          }
          
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/expenditures/properties/${propertyId}/expenditures/items/delete/${expenditure.id}`, {
            method: 'DELETE',
            headers: { 
              'Authorization': `Bearer ${localStorage.getItem('token')}`, 
              'Content-Type': 'application/json' 
            }
          });

          if (response.ok) {
            const result = await response.json();
            setExpenditures(prev => prev.filter(item => item.id !== expenditure.id));

            // Show detailed success message with deletion count
            const relatedCount = result.data?.relatedRecordsDeleted || 0;
            if (relatedCount > 0) {
              toast.success(`Expenditure "${expenditure.invoice_number}" and ${relatedCount} related records deleted successfully!`);
            } else {
              toast.success(`Expenditure "${expenditure.invoice_number}" deleted successfully!`);
            }

            loadExpenditures(); // Reload data to update totals
          } else {
            let message = 'Failed to delete expenditure';
            try {
              const errorData = await response.json();
              message = errorData.message || message;
            } catch (_) {
              if (response.status === 404) message = 'Delete endpoint not found.';
              else if (response.status >= 500) message = 'Server error. Please try again.';
            }
            throw new Error(message);
          }
        }
      } catch (error) {
        console.error('Error removing expenditure:', error);
        toast.error(`Failed to remove expenditure: ${error.message}`);
      }
    }
  };

  const handleVoid = async (expenditure) => {
    try {
      if (useMockData) {
        // For mock data, toggle void status locally
        setExpenditures(prev => prev.map(item => 
          item.id === expenditure.id 
            ? { ...item, void: !item.void }
            : item
        ));
        toast.success(`Expenditure ${expenditure.void ? 'unvoided' : 'voided'} successfully`);
      } else {
        // For real API
        await expendituresService.toggleVoid(expenditure.id);
        setExpenditures(prev => prev.map(item => 
          item.id === expenditure.id 
            ? { ...item, void: !item.void }
            : item
        ));
        toast.success(`Expenditure ${expenditure.void ? 'unvoided' : 'voided'} successfully`);
      }
    } catch (error) {
      console.error('Error toggling void status:', error);
      toast.error('Failed to toggle void status');
    }
  };

  // Utility functions
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    // Handle DD/MM/YYYY format (from backend)
    if (typeof dateString === 'string' && dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        
        // Validate date parts
        if (!isNaN(day) && !isNaN(month) && !isNaN(year) && day > 0 && day <= 31 && month > 0 && month <= 12) {
          const date = new Date(year, month - 1, day);
          // Verify the date is valid (handles invalid dates like 31/02/2025)
          if (date.getDate() === day && date.getMonth() === month - 1 && date.getFullYear() === year) {
            return date.toLocaleDateString('en-GB');
          }
        }
      }
    }
    
    // Try standard Date parsing (for ISO format or other formats)
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-GB');
    }
    
    // If all parsing fails, return the original string or '-'
    return '-';
  };

  const formatCurrency = (amount) => {
    return `£${parseFloat(amount).toFixed(2)}`;
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxPages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPages / 2));
    let endPage = Math.min(totalPages, startPage + maxPages - 1);
    
    if (endPage - startPage + 1 < maxPages) {
      startPage = Math.max(1, endPage - maxPages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  // Click outside to close column visibility dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showColumnVisibility && !event.target.closest(`.${styles.visibilityBtnContainer}`)) {
        setShowColumnVisibility(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnVisibility]);

  // Fetch Xero payment status for the rows on screen. One call per page rather than per row,
  // and a failure is silent — this is supplementary information and must never stop the
  // expenditure list rendering.
  //
  // Placed ABOVE the `if (loading)` return below: a hook after an early return is skipped on
  // the renders that take it, which breaks the rules of hooks. Ids are read from
  // getFilteredExpenditures directly rather than from currentData, which is derived after
  // that return.
  const visibleExpenditureIds = useMemo(
    () => (getFilteredExpenditures || []).map((e) => e.id).filter(Boolean),
    [getFilteredExpenditures]
  );

  useEffect(() => {
    if (!columnVisibility.xeroPayment || !visibleExpenditureIds.length) {
      setXeroPayments({});
      return;
    }

    let cancelled = false;
    getPaymentStatus(visibleExpenditureIds)
      .then((res) => {
        if (!cancelled && res?.success) setXeroPayments(res.data || {});
      })
      .catch(() => {
        /* supplementary only — leave the column blank */
      });

    return () => {
      cancelled = true;
    };
  }, [visibleExpenditureIds, columnVisibility.xeroPayment]);

  if (loading) {
    return <div className={styles.loading}>Loading expenditures...</div>;
  }

  const currentData = getCurrentPageData();

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        <span onClick={() => navigate('/dashboard')} className={styles.breadcrumbLink}>Dashboard</span>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span className={styles.breadcrumbCurrent}>Expenditures</span>
      </div>

      {/* Debug Toggle */}
      <div className={styles.debugToggle}>
        <label>
          <input
            type="checkbox"
            checked={useMockData}
            onChange={(e) => setUseMockData(e.target.checked)}
          />
          Use Mock Data
        </label>
      </div>

      {/* Main Panel */}
      <div className={styles.panel}>
        <div className={styles.panelTitle}>
          <h3 className={styles.panelTitleText}>Expenditures</h3>
          <button 
            className={styles.addButton}
            onClick={() => navigate('/expenditures/create')}
          >
            <i className="glyphicon glyphicon-plus"></i> Add expenditure
          </button>
        </div>

        {/* Filters */}
        <div className={styles.filtersSection}>
          <form onSubmit={handleSearch} className={styles.filtersForm}>
            <div className={styles.filtersRow}>
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Category</label>
                <select
                  className={styles.filterSelect}
                  value={filters.category_id}
                  onChange={(e) => handleFilterChange('category_id', e.target.value)}
                >
                  <option value="">All</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Type</label>
                <select
                  className={styles.filterSelect}
                  value={filters.type_id}
                  onChange={(e) => handleFilterChange('type_id', e.target.value)}
                >
                  <option value="">All</option>
                  {types.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Bank Account</label>
                <select
                  className={styles.filterSelect}
                  value={filters.bank_account_id}
                  onChange={(e) => handleFilterChange('bank_account_id', e.target.value)}
                >
                  <option value="">All</option>
                  {bankAccounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Creditor</label>
                <input
                  type="text"
                  className={styles.filterInput}
                  value={filters.creditor}
                  onChange={(e) => handleFilterChange('creditor', e.target.value)}
                  placeholder="Search creditor..."
                />
              </div>

              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Date from</label>
                <input
                  type="date"
                  className={styles.filterInput}
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                />
              </div>

              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Date to</label>
                <input
                  type="date"
                  className={styles.filterInput}
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                />
              </div>
            </div>

            <div className={styles.searchButtonContainer}>
              <button type="submit" className={styles.searchButton}>
                Search
              </button>
              <button 
                type="button" 
                className={styles.clearButton}
                onClick={handleClearFilters}
              >
                Clear Filters
              </button>
            </div>
          </form>
        </div>

        {/* DataTables Controls */}
        <div className={styles.dataTableControls}>
          <div className={styles.entriesControl}>
            <label>
              Show{' '}
              <select
                value={tableControls.entriesPerPage}
                onChange={(e) => handleTableControlChange('entriesPerPage', e.target.value)}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="30">30</option>
                <option value="50">50</option>
                <option value="75">75</option>
                <option value="100">100</option>
              </select>{' '}
              entries
            </label>
          </div>

          <div className={styles.searchControl}>
            <label>
              Search:{' '}
              <input
                ref={searchInputRef}
                type="text"
                value={tableControls.search}
                onChange={handleSearchInputChange}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                placeholder="Search expenditures..."
                autoComplete="off"
              />
            </label>
          </div>

          <div className={styles.actionButtons}>
            <button 
              className={styles.exportBtn}
              onClick={() => {
                loadExpenditures();
                toast.success('Data refreshed');
              }}
              title="Refresh data"
            >
              Refresh
            </button>
            <button 
              className={styles.exportBtn}
              onClick={handleExportCSV}
              title="Export as CSV"
            >
              Export as CSV
            </button>
            <button 
              className={styles.exportBtn}
              onClick={handleExportPDF}
              title="Export as PDF"
            >
              Export as PDF
            </button>
            <button 
              className={styles.exportBtn}
              onClick={handlePrintTable}
              title="Print table"
            >
              Print table
            </button>
            <div className={styles.visibilityBtnContainer}>
              <button 
                className={styles.visibilityBtn}
                onClick={handleColumnVisibility}
              >
                Column visibility
              </button>
              {showColumnVisibility && (
                <div className={styles.columnVisibilityDropdown}>
                  <h4>Toggle Columns:</h4>
                  <label className={styles.columnToggle}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.property}
                      onChange={() => toggleColumnVisibility('property')}
                    />
                    Property
                  </label>
                  <label className={styles.columnToggle}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.category}
                      onChange={() => toggleColumnVisibility('category')}
                    />
                    Category
                  </label>
                  <label className={styles.columnToggle}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.type}
                      onChange={() => toggleColumnVisibility('type')}
                    />
                    Type
                  </label>
                  <label className={styles.columnToggle}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.bankAccount}
                      onChange={() => toggleColumnVisibility('bankAccount')}
                    />
                    Bank Account
                  </label>
                  <label className={styles.columnToggle}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.amountPaid}
                      onChange={() => toggleColumnVisibility('amountPaid')}
                    />
                    Amount Paid
                  </label>
                  <label className={styles.columnToggle}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.date}
                      onChange={() => toggleColumnVisibility('date')}
                    />
                    Date
                  </label>
                  <label className={styles.columnToggle}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.creditor}
                      onChange={() => toggleColumnVisibility('creditor')}
                    />
                    Creditor
                  </label>
                  <label className={styles.columnToggle}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.invoice}
                      onChange={() => toggleColumnVisibility('invoice')}
                    />
                    Invoice
                  </label>
                  <label className={styles.columnToggle}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.ticketId}
                      onChange={() => toggleColumnVisibility('ticketId')}
                    />
                    Ticket ID
                  </label>
                  <label className={styles.columnToggle}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.order}
                      onChange={() => toggleColumnVisibility('order')}
                    />
                    Order
                  </label>
                  <label className={styles.columnToggle}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.xeroPayment}
                      onChange={() => toggleColumnVisibility('xeroPayment')}
                    />
                    In Xero
                  </label>
                  <label className={styles.columnToggle}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.actions}
                      onChange={() => toggleColumnVisibility('actions')}
                    />
                    Actions
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className={styles.tableContainer} style={{ position: 'relative' }}>
          {tableLoading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10
            }}>
              <span>Loading...</span>
            </div>
          )}
          <table className={styles.expendituresTable}>
            <thead>
              <tr>
                {columnVisibility.property && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("property")}
                    style={{ cursor: "pointer" }}
                  >
                    Property<SortIcon direction={sortField === "property" ? sortDirection : null} isActive={sortField === "property"} />
                  </th>
                )}
                {columnVisibility.category && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("category")}
                    style={{ cursor: "pointer" }}
                  >
                    Category<SortIcon direction={sortField === "category" ? sortDirection : null} isActive={sortField === "category"} />
                  </th>
                )}
                {columnVisibility.type && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("type")}
                    style={{ cursor: "pointer" }}
                  >
                    Type<SortIcon direction={sortField === "type" ? sortDirection : null} isActive={sortField === "type"} />
                  </th>
                )}
                {columnVisibility.bankAccount && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("bankAccount")}
                    style={{ cursor: "pointer" }}
                  >
                    Bank Account<SortIcon direction={sortField === "bankAccount" ? sortDirection : null} isActive={sortField === "bankAccount"} />
                  </th>
                )}
                {columnVisibility.amountPaid && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("amountPaid")}
                    style={{ cursor: "pointer" }}
                  >
                    Amount Paid<SortIcon direction={sortField === "amountPaid" ? sortDirection : null} isActive={sortField === "amountPaid"} />
                  </th>
                )}
                {columnVisibility.date && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("date")}
                    style={{ cursor: "pointer" }}
                  >
                    Date<SortIcon direction={sortField === "date" ? sortDirection : null} isActive={sortField === "date"} />
                  </th>
                )}
                {columnVisibility.creditor && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("creditor")}
                    style={{ cursor: "pointer" }}
                  >
                    Creditor<SortIcon direction={sortField === "creditor" ? sortDirection : null} isActive={sortField === "creditor"} />
                  </th>
                )}
                {columnVisibility.invoice && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("invoice")}
                    style={{ cursor: "pointer" }}
                  >
                    Invoice<SortIcon direction={sortField === "invoice" ? sortDirection : null} isActive={sortField === "invoice"} />
                  </th>
                )}
                {columnVisibility.ticketId && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("ticketId")}
                    style={{ cursor: "pointer" }}
                  >
                    Ticket ID<SortIcon direction={sortField === "ticketId" ? sortDirection : null} isActive={sortField === "ticketId"} />
                  </th>
                )}
                {columnVisibility.order && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("order")}
                    style={{ cursor: "pointer" }}
                  >
                    Order<SortIcon direction={sortField === "order" ? sortDirection : null} isActive={sortField === "order"} />
                  </th>
                )}
                {columnVisibility.xeroPayment && (
                  <th title="What Xero shows for this invoice. Read-only.">In Xero</th>
                )}
                {columnVisibility.actions && <th className={styles.actionHead}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {currentData.length === 0 ? (
                <tr>
                  <td colSpan={Object.values(columnVisibility).filter(Boolean).length} className={styles.noDataCell}>
                    No expenditures found
                  </td>
                </tr>
              ) : (
                currentData.map(expenditure => (
                  <tr key={expenditure.id}>
                    {columnVisibility.property && (
                      <td>
                        <a 
                          href={`/properties/${expenditure.propertyId}`}
                          className={styles.propertyLink}
                        >
                          {expenditure.propertyName}
                        </a>
                      </td>
                    )}
                    {columnVisibility.category && <td>{expenditure.categoryName}</td>}
                    {columnVisibility.type && <td>{expenditure.typeName}</td>}
                    {columnVisibility.bankAccount && <td>{expenditure.bankAccountName}</td>}
                    {columnVisibility.amountPaid && (
                      <td className={styles.amountCell}>
                        {formatCurrency(expenditure.amount_paid)}
                      </td>
                    )}
                    {columnVisibility.date && <td>{formatDate(expenditure.date)}</td>}
                    {columnVisibility.creditor && <td>{expenditure.creditor}</td>}
                    {columnVisibility.invoice && <td>{expenditure.invoice_number}</td>}
                    {columnVisibility.ticketId && (
                      <td>
                        {expenditure.ticket_id ? (
                          <a 
                            href={`/tickets/${expenditure.ticket_id}`}
                            className={styles.ticketLink}
                          >
                            {expenditure.ticket_id}
                          </a>
                        ) : ''}
                      </td>
                    )}
                    {columnVisibility.order && <td>{expenditure.purchase_order_number}</td>}
                    {columnVisibility.xeroPayment && (
                      <td>
                        {(() => {
                          const x = xeroPayments[expenditure.id];
                          // No entry means this record has no matching bill in Xero —
                          // which is itself worth seeing, and appears on the comparison view.
                          if (!x) return <span style={{ color: '#adb5bd' }}>—</span>;

                          const label =
                            x.payment_state === 'paid'
                              ? 'Paid'
                              : x.payment_state === 'part_paid'
                              ? 'Part paid'
                              : 'Outstanding';
                          const colour =
                            x.payment_state === 'paid'
                              ? '#198754'
                              : x.payment_state === 'part_paid'
                              ? '#fd7e14'
                              : '#6c757d';

                          const title = [
                            `Xero: ${label}`,
                            x.amount_paid ? `£${Number(x.amount_paid).toFixed(2)}` : null,
                            x.fully_paid_on
                              ? `on ${new Date(x.fully_paid_on).toLocaleDateString('en-GB')}`
                              : null,
                            // The account Xero paid from — the one thing MyGlobe cannot
                            // work out for itself.
                            x.payment_account_name ? `from ${x.payment_account_name}` : null,
                          ]
                            .filter(Boolean)
                            .join(' ');

                          return (
                            <a
                              href={x.xero_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={title}
                              style={{ color: colour, textDecoration: 'none', fontWeight: 600 }}
                            >
                              {label}
                            </a>
                          );
                        })()}
                      </td>
                    )}
                    {columnVisibility.actions && (
                      <td className={styles.actionCell}>
                        <div className={styles.actionButtonsGroup}>
                          <button
                            className={styles.actionBtn}
                            onClick={() => handleShow(expenditure.id)}
                            title="Show"
                          >
                            Show
                          </button>
                          <button
                            className={styles.actionBtn}
                            onClick={() => handleEdit(expenditure)}
                            title="Edit"
                          >
                            Edit
                          </button>
                          {expenditure.file && (
                            <button
                              className={styles.actionBtn}
                              onClick={() => handleDownload(expenditure)}
                              title="Download"
                            >
                              Download
                            </button>
                          )}
                          <button
                            className={styles.actionBtn}
                            onClick={() => handleRemove(expenditure)}
                            title="Remove"
                          >
                            Remove
                          </button>
                          <button
                            className={`${styles.actionBtn} ${expenditure.void ? styles.voidYellow : styles.voidWhite}`}
                            onClick={() => handleVoid(expenditure)}
                            title="Void"
                          >
                            Void
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className={styles.pagination}>
          <div className={styles.paginationInfo}>
            Showing {((currentPage - 1) * parseInt(tableControls.entriesPerPage, 10)) + 1} to{' '}
            {Math.min(currentPage * parseInt(tableControls.entriesPerPage, 10), totalRecords)} of{' '}
            {totalRecords} entries
          </div>
          
          <div className={styles.paginationControls}>
            <button
              className={styles.paginationBtn}
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            
            {getPageNumbers().map(page => (
              <button
                key={page}
                className={`${styles.paginationBtn} ${page === currentPage ? styles.activePage : ''}`}
                onClick={() => goToPage(page)}
              >
                {page}
              </button>
            ))}
            
            <button
              className={styles.paginationBtn}
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Expenditures; 